import React, { useState } from "react";
import { Download, Loader2, QrCode, Undo2 } from "lucide-react";
import { AppButton, AppCard, ConfirmModal, EmptyState, StatusBadge } from "../ui";
import { cancelMyQrPayment, downloadMyQrInvoice, saveBlob } from "../../utils/qrPaymentApi";

const PURPOSE_LABEL = {
  batch_enrollment: "Course enrolment",
  self_paced_purchase: "Self-paced course",
  fee_payment: "Fee payment",
};

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const when = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

/**
 * The student's own UPI QR payments, newest first: what is still waiting, what
 * was approved (with its invoice), and what was rejected and why. A waiting
 * payment can be withdrawn — the honest fix for a wrong screenshot is to take it
 * back and send the right one, not to send a second claim beside it.
 */
export default function MyQrPaymentsList({ payments, onChanged, onToast }) {
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);

  if (!payments?.length) {
    return (
      <EmptyState
        icon={<QrCode size={40} />}
        title="No UPI QR payments yet"
        description="When you pay your institute's QR code and upload the screenshot, it shows here until it's approved."
      />
    );
  }

  const withdraw = async () => {
    const p = confirm;
    setBusyId(p._id);
    try {
      await cancelMyQrPayment(p._id);
      onToast?.("Payment withdrawn. You can send it again with the right details.");
      setConfirm(null);
      onChanged?.();
    } catch (err) {
      onToast?.(err?.response?.data?.message || "Couldn't withdraw the payment.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const invoice = async (p) => {
    setBusyId(p._id);
    try {
      saveBlob(await downloadMyQrInvoice(p._id), `${p.invoiceNumber || "invoice"}.pdf`);
    } catch (err) {
      onToast?.("Couldn't download the invoice. Please try again.", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-p-0">
        {payments.map((p) => (
          <AppCard
            as="li"
            key={p._id}
            className="tw-grid tw-grid-cols-[minmax(0,1fr)_auto] tw-items-center tw-gap-x-4 tw-gap-y-2 tw-px-4 tw-py-3"
          >
            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
              <span className="tw-break-words tw-text-[13.5px] tw-font-semibold tw-text-content">
                {p.course?.name || PURPOSE_LABEL[p.purpose]}
              </span>
              <span className="tw-break-words tw-text-xs tw-text-content-muted">
                {PURPOSE_LABEL[p.purpose]} · UTR {p.utr} · sent {when(p.createdAt)}
                {p.invoiceNumber ? ` · ${p.invoiceNumber}` : ""}
              </span>
              {p.status === "rejected" && p.rejectReason && (
                <span className="tw-text-xs tw-text-danger-fg">Not approved: {p.rejectReason}</span>
              )}
              {p.status === "approved" && p.approvedAmount != null && p.approvedAmount < p.amount && (
                <span className="tw-text-xs tw-text-content-muted">
                  {money(p.approvedAmount)} of the {money(p.amount)} you sent was confirmed.
                </span>
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
              <span className="tw-whitespace-nowrap tw-text-[14.5px] tw-font-medium tw-text-content">
                {money(p.status === "approved" ? p.approvedAmount ?? p.amount : p.amount)}
              </span>
              <StatusBadge status={p.status} domain="qrPayment" />
            </div>
            {(p.status === "submitted" || p.status === "approved") && (
              <div className="tw-col-span-full tw-flex tw-gap-2">
                {p.status === "submitted" && (
                  <AppButton variant="ghost" size="sm" onClick={() => setConfirm(p)} disabled={busyId === p._id}>
                    <Undo2 size={14} /> Withdraw
                  </AppButton>
                )}
                {p.status === "approved" && p.invoiceNumber && (
                  <AppButton
                    variant="outline"
                    size="sm"
                    onClick={() => invoice(p)}
                    disabled={busyId === p._id}
                    aria-label={`Download invoice ${p.invoiceNumber}`}
                  >
                    {busyId === p._id ? <Loader2 size={14} className="tw-animate-spin" /> : <Download size={14} />} Invoice
                  </AppButton>
                )}
              </div>
            )}
          </AppCard>
        ))}
      </ul>

      <ConfirmModal
        show={!!confirm}
        onHide={() => setConfirm(null)}
        onConfirm={withdraw}
        title="Withdraw this payment?"
        message="Your institute won't review it. Do this if you uploaded the wrong screenshot or ID — then send it again. It doesn't refund money you paid."
        confirmText="Withdraw"
        cancelText="Keep it"
        variant="warning"
      />
    </>
  );
}
